import { create } from 'zustand';
import { createSelector } from 'reselect'; // Importa createSelector
import type { GeoDataState, LayerBase, LayerStyleProperties } from './types'; // Importa l'interfaccia combinata e tipi utili
import { createLayerSlice } from './layerSlice';
import { createLayerStyleSlice } from './layerStyleSlice';
import { createMapViewSlice } from './mapViewSlice';
import { createBasemapSlice } from './basemapSlice';
import { createMapControlsSlice } from './mapControlsSlice';
import { createAttributeTableSlice } from './attributeTableSlice';
import { createSessionSlice } from './sessionSlice';
import { createRightPanelSlice } from './rightPanelSlice'; // Importa il nuovo slice
import { createContoursSlice } from './contoursSlice'; // Importa ContoursSlice
import { createPrintMapsSlice } from './printMapsSlice'; // Importa PrintMapsSlice

// Crea lo store combinato importando e unendo tutte le slice
// Corretta la firma della funzione di creazione dello store
const useGeoData = create<GeoDataState>()((set, get, api) => ({
  ...createLayerSlice(set, get, api),
  ...createLayerStyleSlice(set, get, api),
  ...createMapViewSlice(set, get, api),
  ...createBasemapSlice(set, get, api),
  ...createMapControlsSlice(set, get, api),
  ...createAttributeTableSlice(set, get, api),
  ...createRightPanelSlice(set, get, api), // Aggiungi il nuovo slice
  ...createSessionSlice(set, get, api),
  ...createContoursSlice(set, get, api), // Aggiungi ContoursSlice qui
  ...createPrintMapsSlice(set, get, api), // Aggiungi PrintMapsSlice qui
  // Riferimenti al Temporal Controller rimossi
}));

// --- Selettori Helper ---

// --- Selettori Input Memoizzati ---
const selectDataLayers = (state: GeoDataState) => state.dataLayers;
const selectLayerStyles = (state: GeoDataState) => state.layerStyles;
const selectLayerId = (_state: GeoDataState, layerId: string) => layerId;

// Selettore per ottenere tutti i layer dell'utente (attualmente contenuti in dataLayers)
export const selectAllUserLayers = (state: GeoDataState): LayerBase[] => state.dataLayers;

/**
 * Selettore MEMOIZZATO per ottenere i dati completi di un layer, combinando
 * le informazioni di base (LayerBase) con le proprietà di stile (LayerStyleProperties).
 * Restituisce undefined se il layer o i suoi stili non vengono trovati.
 * Usa reselect per evitare ricalcoli non necessari.
 */
export const selectFullLayerData = createSelector(
  [selectDataLayers, selectLayerStyles, selectLayerId],
  (dataLayers, layerStyles, layerId): (LayerBase & LayerStyleProperties) | undefined => {
    const baseLayer = dataLayers.find(layer => layer.id === layerId);
    const styles = layerStyles[layerId];

    if (!baseLayer) {
      return undefined;
    }
    if (!styles) {
      return undefined;
    }

    // La combinazione avviene solo se gli input (layers, styles, layerId) cambiano
    return {
      ...baseLayer,
      ...styles,
    };
  }
);

/**
 * Selettore MEMOIZZATO per ottenere tutti i dati completi dei layer visibili.
 * Utile per componenti come MapView che devono renderizzare tutti i layer.
 */
export const selectVisibleFullLayers = createSelector(
  [selectDataLayers, selectLayerStyles], // Dipende dagli array di layer e stili
  (dataLayers, layerStyles): (LayerBase & LayerStyleProperties)[] => {
    return dataLayers
      .filter(layer => layer.visible) // Filtra solo i layer visibili
      .map(layer => {
        // Combina base e stile manually qui per evitare dipendenza ciclica con selectFullLayerData
        // o la necessità di passare lo stato completo a map.
        const styles = layerStyles[layer.id];
        if (!styles) return undefined; // Salta se mancano gli stili
        return { ...layer, ...styles };
      })
      .filter((layer): layer is (LayerBase & LayerStyleProperties) => layer !== undefined); // Filtra eventuali undefined
  }
);

export default useGeoData;